import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Pliable API',
  content: `
Pliable exposes a set of APIs to interact with the platform. The docs below explain these features.

## Generate API credentials

If you are an account admin in Pliable, you can go to the user menu in the top right, choose **Manage Users** and then **API Tokens**. Click **Generate Token**, choose the "Builder" role and then save your credentials in a secure place.

### Retrieve an Access Token for your credentials

Using your account id and the \`client_id\` and \`client_secret\` you created in the previous step, you can obtain an access token with a POST to the following endpoint:

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/authenticate" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_id": "acme_co",
    "client_id": "XXXXX",
    "client_secret": "XXXX"
}'
\`\`\`

This call returns JSON that contains an \`access_token\` that you will use to access any further API calls.

### Restrict what a token can see

If the token will query data on behalf of one of *your* users (a customer portal, a partner integration), pass a \`data_scope\` so it can only see that user's rows and metrics:

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/authenticate" \\
  -H "Content-Type: application/json" \\
  -d '{
    "account_id": "acme_co",
    "client_id": "XXXXX",
    "client_secret": "XXXX",
    "data_scope": {
      "row_restrictions": [
        {"dimension": "Region", "comparator": "IN", "value": ["US", "Canada"]},
        {"dimension": "Employee Type", "comparator": "NOT_EQUALS", "value": "Executive",
         "applies_to_metrics": ["Total Pay"]}
      ],
      "blacklisted_metrics": ["Internal Margin"]
    }
}'
\`\`\`

| Field | Description |
|---|---|
| \`data_scope.row_restrictions\` | Restrictions applied to every query made with the token. They combine with AND. Each one is \`dimension\` (name or ID), \`comparator\` (default \`IN\`), \`value\`, and optionally \`applies_to_metrics\` and \`unreachable\`. See [Embedding Pliable](/docs/embedding-pliable) for the full field reference. |
| \`data_scope.blacklisted_metrics\` | Metric names or IDs the token can't see at all. |
| \`data_scope.dimension_filters\` | **Deprecated — use \`row_restrictions\`.** \`{"Region": ["US", "Canada"]}\`, applied as an \`IN\` filter. Still supported, and can be combined with \`row_restrictions\`. |
| \`branch\` | \`PROD\` (default) or \`DEVELOP\`. Names in \`data_scope\` are looked up on this branch. |
| \`external_user_id\` | Optional ID of your end user, so Ellie conversations are attributed to them. |

A scoped token must have at least one row restriction (or a deprecated \`dimension_filters\` entry). Only the restrictions' existence is checked: one with \`applies_to_metrics\` covers only the metrics it names, and the token sees every other metric unrestricted. It is read-only, lasts one hour and can't be revoked, so mint a fresh one per session. Unknown dimension or metric names, unsupported comparators, and a metric in \`applies_to_metrics\` that isn't connected to the restricted dimension are all rejected with a 400.

The scope is enforced on everything the token calls: the analyses endpoints below, \`GET /api/v1/ext/metrics\` (hidden metrics aren't listed), and Ellie.

**Testing a scope against unpublished changes.** Mint the token with \`"branch": "DEVELOP"\` from your test environment: the scope is checked and enforced against Develop, so it behaves the way it will once Develop is published. Production tokens stay on \`PROD\`. Make the switch part of your release: publish in Pliable first, then deploy the integration change that depends on it, since a scope naming something not yet published is rejected on \`PROD\`.

## Run an analysis

With an access token (scoped or not), you can compute metrics directly:

1. **Find what's available.** \`GET /api/v1/ext/dimensions\` and \`GET /api/v1/ext/metrics\` list names and IDs (paginated with \`page\` and \`page_size\`, max 100).
2. **Queue a build.** \`POST /api/v1/ext/analyses\`:

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/ext/analyses" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "dimension_ids": ["Region"],
    "measure_ids": ["Total Sales"],
    "dimension_filters": [{"dimension_id": "Channel", "comparator": "IN", "value": ["Online"]}],
    "date_range": "LAST_30_DAYS"
}'
\`\`\`

This returns an \`analysis_id\` and a \`build_id\`. \`dimension_filters\` here narrows this one analysis, on top of the token's \`data_scope\`; it isn't the deprecated \`data_scope\` field.
3. **Wait for it.** Poll \`GET /api/v1/ext/analyses/builds/{build_id}\` until \`status\` is \`complete\`.
4. **Read the rows.** \`GET /api/v1/ext/analyses/{analysis_id}/data\`. The token's \`data_scope\` is applied again at read time.

The full request and response schemas are in the API reference.

## API Workflows

- [Uploading files via API](/docs/uploading-files-via-api)
- [Programmatic User Management](/docs/programmatic-user-management)
`,
};

export default doc;
